import { useLayoutEffect, type RefObject } from 'react'
import { fetchWithAuth } from '@/lib/api'

/**
 * Blob URLs for Basecamp previews, keyed by the remote download URL.
 * Kept for the page session. Revoking them when this effect re-runs
 * (draft html refresh, parent re-render) leaves <img> pointing at a dead
 * blob, which is the broken-image flash after a successful load.
 */
const hydratedBlobs = new Map<string, string>()

function applyCachedBlob(img: HTMLImageElement, remote: string): boolean {
  const cached = hydratedBlobs.get(remote)
  if (!cached) return false
  img.src = cached
  img.closest('figure')?.removeAttribute('data-bc-image-failed')
  return true
}

/** Load token-gated Basecamp blobs into <img data-bc-image-url>. */
export function useHydrateBasecampImages(
  containerRef: RefObject<HTMLElement | null>,
  html: string,
) {
  useLayoutEffect(() => {
    const root = containerRef.current
    if (!root || !html) return

    let cancelled = false
    const imgs = Array.from(
      root.querySelectorAll<HTMLImageElement>('img[data-bc-image-url]'),
    )

    void Promise.all(
      imgs.map(async (img) => {
        const remote = img.getAttribute('data-bc-image-url')
        if (!remote) return
        if (applyCachedBlob(img, remote)) return
        try {
          const res = await fetchWithAuth(
            `/api/basecamp/attachment-image?url=${encodeURIComponent(remote)}`,
          )
          if (cancelled) return
          if (!res.ok) {
            console.warn('[basecamp] attachment image failed', res.status, remote)
            img.closest('figure')?.setAttribute('data-bc-image-failed', 'true')
            return
          }
          const blob = await res.blob()
          if (cancelled) return
          if (blob.type && !blob.type.startsWith('image/') && blob.type !== 'application/octet-stream') {
            console.warn('[basecamp] attachment image not an image', blob.type, remote)
            img.closest('figure')?.setAttribute('data-bc-image-failed', 'true')
            return
          }
          const objectUrl = URL.createObjectURL(blob)
          hydratedBlobs.set(remote, objectUrl)
          img.src = objectUrl
          img.closest('figure')?.removeAttribute('data-bc-image-failed')
        } catch (err) {
          if (!cancelled) {
            console.warn('[basecamp] attachment image request failed', remote, err)
          }
        }
      }),
    )

    return () => {
      cancelled = true
    }
  }, [containerRef, html])
}
